import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './Distillery.css';

/**
 * 🌊💎 DISTIL PROTOCOL - DISTILLERY VISUAL
 * Normal Mode:  Plasma Crucible (Option 4)
 * Toilet Mode:  Toilet Swirl Vortex (Option A)
 * Built by Human + AI 2026
 *
 * FIXES v3:
 *   - Normal viewBox  480x530 → 480x620  (nodes no longer clipped)
 *   - Toilet viewBox  480x580 → 480x680  (nodes no longer clipped)
 *   - All animation durations increased ~60% for smoother, longer loops
 */

const Distillery = ({
  isActive = false,
  objectCount = 0,
  distillationsCount = 0,
  purityScore = 0,
  isDemoMode = false,
  showStats = false,
  scale = 1,
  onSectionHover
}) => {
  const [flowingParticles, setFlowingParticles] = useState([]);
  const [isToiletMode, setIsToiletMode] = useState(false);

  useEffect(() => {
    const checkToiletMode = () => {
      setIsToiletMode(document.body.classList.contains('toilet-mode'));
    };
    checkToiletMode();
    const observer = new MutationObserver(checkToiletMode);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isActive && objectCount > 0) {
      const particles = Array.from({ length: Math.min(objectCount * 3, 20) }, (_, i) => ({
        id: i,
        delay: i * 0.35,
        path: i % 2 === 0 ? 'left' : 'right'
      }));
      setFlowingParticles(particles);
    } else {
      setFlowingParticles([]);
    }
  }, [isActive, objectCount]);

  return (
    <div className="distillery-container">
      <div className={`distillery-glow distillery-glow-top ${isToiletMode ? 'toilet-glow' : ''}`}></div>
      <div className={`distillery-glow distillery-glow-middle ${isToiletMode ? 'toilet-glow' : ''}`}></div>
      <div className={`distillery-glow distillery-glow-bottom ${isToiletMode ? 'toilet-glow' : ''}`}></div>

      <div style={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}>
        {isToiletMode ? (
          <ToiletSwirlVortex
            isActive={isActive}
            objectCount={objectCount}
            distillationsCount={distillationsCount}
            flowingParticles={flowingParticles}
          />
        ) : (
          <PlasmaCrucible
            isActive={isActive}
            objectCount={objectCount}
            distillationsCount={distillationsCount}
            purityScore={purityScore}
            flowingParticles={flowingParticles}
          />
        )}
      </div>

      {showStats && (
        <div className="distillery-stats">
          {isDemoMode && (
            <motion.div
              className="stat-item demo-mode-indicator"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <span className="stat-label">Mode</span>
              <span className="stat-value demo">🎮 DEMO</span>
            </motion.div>
          )}
          {isToiletMode && (
            <motion.div
              className="stat-item toilet-mode-indicator"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <span className="stat-label">Mode</span>
              <span className="stat-value toilet">🚽 TOILET</span>
            </motion.div>
          )}
          <motion.div
            className="stat-item"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span className="stat-label">Active</span>
            <span className={`stat-value ${isActive ? 'active' : 'inactive'}`}>
              {isActive ? '●' : '○'}
            </span>
          </motion.div>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════
// 🌊💎 NORMAL MODE — PLASMA CRUCIBLE
// viewBox expanded to 480x620 so output nodes are fully visible
// ═══════════════════════════════════════════════════════════════════════════

const PlasmaCrucible = ({ isActive, objectCount, distillationsCount, purityScore, flowingParticles }) => {
  return (
    <svg
      viewBox="0 0 480 620"
      className="distillery-svg"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', height: '100%' }}
    >
      <defs>
        <linearGradient id="crucibleBody" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#0f172a" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#020617" stopOpacity="1"    />
        </linearGradient>
        <linearGradient id="crucibleEdge" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#67e8f9" stopOpacity="0.95" />
          <stop offset="50%"  stopColor="#22d3ee" stopOpacity="0.75" />
          <stop offset="100%" stopColor="#0891b2" stopOpacity="0.5"  />
        </linearGradient>
        <linearGradient id="plasmaFill" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#a855f7" stopOpacity="0.65" />
          <stop offset="40%"  stopColor="#22d3ee" stopOpacity="0.75" />
          <stop offset="100%" stopColor="#0891b2" stopOpacity="0.9"  />
        </linearGradient>
        <linearGradient id="toxicPipe" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#8B4513" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#4a2008" stopOpacity="1"   />
        </linearGradient>
        <linearGradient id="fireGradN" x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%"   stopColor="#fff176" stopOpacity="1"    />
          <stop offset="30%"  stopColor="#ffb300" stopOpacity="1"    />
          <stop offset="70%"  stopColor="#f4511e" stopOpacity="0.88" />
          <stop offset="100%" stopColor="#b71c1c" stopOpacity="0.42" />
        </linearGradient>
        <radialGradient id="plasmaCore" cx="50%" cy="40%" r="60%">
          <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.88" />
          <stop offset="25%"  stopColor="#22d3ee" stopOpacity="0.75" />
          <stop offset="60%"  stopColor="#a855f7" stopOpacity="0.45" />
          <stop offset="100%" stopColor="transparent" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="userNodeN" cx="40%" cy="35%" r="60%">
          <stop offset="0%"   stopColor="#22d3ee" stopOpacity="1"   />
          <stop offset="100%" stopColor="#0e7490" stopOpacity="0.5" />
        </radialGradient>
        <radialGradient id="protoNodeN" cx="40%" cy="35%" r="60%">
          <stop offset="0%"   stopColor="#a855f7" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#4c1d95" stopOpacity="0.4" />
        </radialGradient>
        <linearGradient id="outputBeamL" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#22d3ee" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.1" />
        </linearGradient>
        <linearGradient id="outputBeamR" x1="100%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#a855f7" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#a855f7" stopOpacity="0.1" />
        </linearGradient>
        <linearGradient id="centralBeam" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#22d3ee" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.1" />
        </linearGradient>

        <filter id="glowN" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="softN" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="fireN" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="5" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="coinGlowN" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="8" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <clipPath id="crucibleClipN">
          <path d="M 158 170 L 322 170 L 305 310 Q 240 332 175 310 Z" />
        </clipPath>
      </defs>

      {/* TITLE */}
      <text x="240" y="24" textAnchor="middle"
        fill="#22d3ee" fontSize="14" fontFamily="'Courier New', monospace"
        letterSpacing="4" fontWeight="800" opacity="0.88">
        SUI DISTILLATION PROCESS
      </text>

      {/* AMBIENT GLOW */}
      <motion.ellipse cx="240" cy="240" rx="130" ry="100"
        fill="rgba(34,211,238,0.03)"
        animate={{ scaleX: isActive ? [0.985, 1.092, 0.985] : 1, opacity: isActive ? [0.3, 0.6, 0.3] : 0.2 }}
        transition={{ duration: 5, repeat: Infinity }}
      />

      {/* LEFT PIPE */}
      <path d="M 55 138 L 88 154 L 88 196 L 160 196"
        stroke="#1e293b" strokeWidth="16" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <motion.path d="M 55 138 L 88 154 L 88 196 L 160 196"
        stroke="url(#toxicPipe)" strokeWidth="9" fill="none"
        strokeLinecap="round" strokeLinejoin="round"
        strokeDasharray={isActive ? '0' : '10 8'}
        animate={{ opacity: isActive ? [0.65, 1, 0.65] : 0.5 }}
        transition={{ duration: 3.5, repeat: Infinity }}
      />
      <path d="M 55 138 L 88 154 L 88 196 L 160 196"
        stroke="rgba(139,69,19,0.22)" strokeWidth="4" fill="none"
        strokeLinecap="round" strokeLinejoin="round" />
      {isActive && [0, 1].map(i => (
        <motion.circle key={`tdL-${i}`} cx="162" cy="196" r="4" fill="#8B4513" filter="url(#softN)"
          animate={{ x: [0, 6], y: [0, 22], opacity: [0.85, 0], scale: [1, 0.6] }}
          transition={{ duration: 1.3, repeat: Infinity, delay: i * 0.65 }}
        />
      ))}
      <text x="46" y="132" fill="rgba(139,69,19,0.62)" fontSize="9"
        fontFamily="'Courier New', monospace" letterSpacing="1" fontWeight="700">TOXIC INPUT</text>

      {/* RIGHT PIPE */}
      <path d="M 425 138 L 392 154 L 392 196 L 320 196"
        stroke="#1e293b" strokeWidth="16" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <motion.path d="M 425 138 L 392 154 L 392 196 L 320 196"
        stroke="url(#toxicPipe)" strokeWidth="9" fill="none"
        strokeLinecap="round" strokeLinejoin="round"
        strokeDasharray={isActive ? '0' : '10 8'}
        animate={{ opacity: isActive ? [0.55, 0.95, 0.55] : 0.42 }}
        transition={{ duration: 3.5, repeat: Infinity, delay: 0.8 }}
      />
      <path d="M 425 138 L 392 154 L 392 196 L 320 196"
        stroke="rgba(139,69,19,0.22)" strokeWidth="4" fill="none"
        strokeLinecap="round" strokeLinejoin="round" />
      {isActive && [0, 1].map(i => (
        <motion.circle key={`tdR-${i}`} cx="318" cy="196" r="4" fill="#8B4513" filter="url(#softN)"
          animate={{ x: [0, -6], y: [0, 22], opacity: [0.85, 0], scale: [1, 0.6] }}
          transition={{ duration: 1.3, repeat: Infinity, delay: i * 0.65 + 0.45 }}
        />
      ))}
      <text x="346" y="132" fill="rgba(139,69,19,0.62)" fontSize="9"
        fontFamily="'Courier New', monospace" letterSpacing="1" fontWeight="700">TOXIC INPUT</text>

      {/* CRUCIBLE BODY */}
      <motion.path d="M 148 162 L 332 162 L 315 320 Q 240 344 165 320 Z"
        fill="rgba(34,211,238,0.025)"
        animate={{ opacity: isActive ? [0.25, 0.55, 0.25] : 0.18 }}
        transition={{ duration: 4, repeat: Infinity }}
      />
      <path d="M 158 170 L 322 170 L 305 310 Q 240 332 175 310 Z"
        fill="url(#crucibleBody)" stroke="url(#crucibleEdge)" strokeWidth="3" filter="url(#glowN)" />
      <rect x="148" y="158" width="184" height="20" rx="7"
        fill="#0a1120" stroke="url(#crucibleEdge)" strokeWidth="2.5" filter="url(#glowN)" />
      <rect x="153" y="161" width="78" height="9" rx="4" fill="rgba(255,255,255,0.07)" />
      {[170, 200, 240, 280, 310].map((x, i) => (
        <circle key={i} cx={x} cy="168" r="3" fill="rgba(34,211,238,0.38)" />
      ))}

      {/* PLASMA CONTENTS */}
      <motion.path d="M 162 242 Q 240 258 318 242 L 305 310 Q 240 332 175 310 Z"
        fill="url(#plasmaFill)" clipPath="url(#crucibleClipN)"
        animate={{ opacity: isActive ? [0.38, 0.72, 0.38] : 0.28 }}
        transition={{ duration: 3.5, repeat: Infinity }}
      />
      <motion.ellipse cx="240" cy="242" rx="76" ry="11"
        fill="rgba(34,211,238,0.18)" clipPath="url(#crucibleClipN)"
        animate={{ scaleY: isActive ? [1, 1.55, 1] : 1, opacity: isActive ? [0.18, 0.48, 0.18] : 0.12 }}
        transition={{ duration: 3, repeat: Infinity }}
      />
      {isActive && [0, 1, 2, 3, 4].map(i => (
        <motion.circle key={`pb-${i}`}
          cx={185 + i * 16} cy="288" r="5"
          fill={i % 2 === 0 ? 'rgba(34,211,238,0.65)' : 'rgba(168,85,247,0.65)'}
          clipPath="url(#crucibleClipN)"
          animate={{ y: [0, -88], opacity: [0.75, 0], scale: [1, 1.6] }}
          transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.45, ease: 'easeOut' }}
        />
      ))}
      {isActive && (
        <motion.circle cx="240" cy="252" r="52"
          fill="url(#plasmaCore)" filter="url(#glowN)"
          animate={{ scale: [0.885, 1.192, 0.885], opacity: [0.12, 0.38, 0.12] }}
          transition={{ duration: 3.5, repeat: Infinity }}
        />
      )}

      {/* OBJECT COUNT */}
      {objectCount > 0 ? (
        <motion.text x="240" y="278" textAnchor="middle"
          fill="rgba(255,255,255,0.96)" fontSize="54" fontWeight="900"
          fontFamily="'Courier New', monospace" filter="url(#glowN)"
          animate={{ opacity: isActive ? [0.82, 1, 0.82] : 0.95 }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {objectCount}
        </motion.text>
      ) : (
        <text x="240" y="268" textAnchor="middle"
          fill="rgba(34,211,238,0.2)" fontSize="12"
          fontFamily="'Courier New', monospace" letterSpacing="2">
          SELECT OBJECTS
        </text>
      )}

      {/* FLAMES — after wallet approval */}
      {isActive && (
        <g>
          {[0, 1, 2, 3].map(i => (
            <motion.path key={`fl-${i}`}
              fill="url(#fireGradN)" filter="url(#fireN)"
              d={`M ${200+i*16} 332 C ${197+i*16} 316,${207+i*16} 300,${206+i*16} 290 C ${215+i*16} 302,${220+i*16} 316,${216+i*16} 332 Z`}
              animate={{
                d: [
                  `M ${200+i*16} 332 C ${197+i*16} 316,${207+i*16} 300,${206+i*16} 290 C ${215+i*16} 302,${220+i*16} 316,${216+i*16} 332 Z`,
                  `M ${200+i*16} 332 C ${194+i*16} 312,${204+i*16} 294,${208+i*16} 283 C ${217+i*16} 298,${222+i*16} 313,${214+i*16} 332 Z`,
                  `M ${200+i*16} 332 C ${197+i*16} 316,${207+i*16} 300,${206+i*16} 290 C ${215+i*16} 302,${220+i*16} 316,${216+i*16} 332 Z`,
                ],
                opacity: [0.78, 1, 0.78]
              }}
              transition={{ duration: 0.72 + i * 0.22, repeat: Infinity, ease: 'easeInOut' }}
            />
          ))}
          <motion.ellipse cx="240" cy="336" rx="68" ry="10"
            fill="rgba(255,80,0,0.12)"
            animate={{ scaleX: [1, 1.206, 1], opacity: [0.12, 0.26, 0.12] }}
            transition={{ duration: 1.4, repeat: Infinity }}
          />
        </g>
      )}

      {/* CENTRAL BEAM */}
      <motion.line x1="240" y1="332" x2="240" y2="372"
        stroke="url(#centralBeam)" strokeWidth={isActive ? 6 : 3}
        fill="none" filter="url(#softN)"
        animate={{ opacity: isActive ? [0.5, 1, 0.5] : 0.4, strokeWidth: isActive ? [6, 8, 6] : 3 }}
        transition={{ duration: 5, repeat: Infinity }}
      />
      {isActive && [0, 1, 2].map(i => (
        <motion.circle key={`beam-${i}`} r="4" cx="240" cy="332"
          fill="#22d3ee" filter="url(#softN)"
          animate={{ y: [0, 40], opacity: [0, 1, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.85 }}
        />
      ))}

      {/* SPLIT PATHS — endpoints match node centres at (128,560) and (352,560) */}
      <motion.path d="M 240 372 L 128 560"
        stroke="url(#outputBeamL)" strokeWidth="3.5" fill="none"
        strokeDasharray={isActive ? '0' : '8 6'} filter="url(#softN)"
        animate={{ opacity: isActive ? [0.5, 1, 0.5] : 0.4 }}
        transition={{ duration: 3.5, repeat: Infinity }}
      />
      <motion.path d="M 240 372 L 352 560"
        stroke="url(#outputBeamR)" strokeWidth="3" fill="none"
        strokeDasharray={isActive ? '0' : '8 6'} filter="url(#softN)"
        animate={{ opacity: isActive ? [0.4, 0.88, 0.4] : 0.35 }}
        transition={{ duration: 3.5, repeat: Infinity, delay: 0.5 }}
      />
      <circle cx="240" cy="372" r="6" fill="rgba(34,211,238,0.7)" filter="url(#softN)" />

      {/* FLOWING PARTICLES — 3-point path exactly on beam lines to node centres */}
      {flowingParticles.map(particle => (
        <motion.circle key={particle.id} r="5"
          fill={particle.path === 'left' ? '#22d3ee' : '#a855f7'}
          filter="url(#softN)"
          animate={{
            cx: [240, particle.path === 'left' ? 184 : 296, particle.path === 'left' ? 128 : 352],
            cy: [372, 466, 560],
            opacity: [0, 1, 0],
            scale: [1, 1.4, 0.5]
          }}
          transition={{ duration: 5.5, delay: particle.delay, repeat: Infinity, repeatDelay: 4, ease: 'easeInOut' }}
        />
      ))}

      {/* 92% USER NODE — cy moved down to 560 so it fits in 620 viewBox */}
      {isActive && [1, 2].map(i => (
        <motion.circle key={`urN-${i}`} cx="128" cy="560" r="57"
          fill="none" stroke="#22d3ee" strokeWidth="2"
          animate={{ scale: [1, 1.47], opacity: [0.5, 0] }}
          transition={{ duration: 4, repeat: Infinity, delay: i * 1.6 }}
        />
      ))}
      <motion.circle cx="128" cy="560" r="57"
        fill="rgba(2,6,23,0.94)" stroke="#22d3ee" strokeWidth="3"
        filter="url(#coinGlowN)"
        animate={{ scale: isActive ? [1, 1.06, 1] : 1, opacity: isActive ? [0.86, 1, 0.86] : 0.75 }}
        transition={{ duration: 4, repeat: Infinity }}
        style={{ transformOrigin: '128px 560px' }}
      />
      <circle cx="128" cy="560" r="48" fill="url(#userNodeN)" opacity="0.55" />
      <ellipse cx="113" cy="542" rx="14" ry="18" fill="rgba(255,255,255,0.13)" />
      <text x="128" y="553" textAnchor="middle" fill="#ffffff" fontSize="28" fontWeight="900"
        fontFamily="'Courier New', monospace" filter="url(#glowN)">92%</text>
      <text x="128" y="574" textAnchor="middle" fill="#67e8f9" fontSize="11" fontWeight="700"
        fontFamily="'Courier New', monospace" letterSpacing="2">💎 USER</text>

      {/* 8% PROTOCOL NODE — cy moved down to 560 */}
      <motion.circle cx="352" cy="560" r="46"
        fill="rgba(2,6,23,0.91)" stroke="#a855f7" strokeWidth="2.5"
        filter="url(#coinGlowN)"
        animate={{ scale: isActive ? [1, 1.05, 1] : 1, opacity: isActive ? [0.76, 0.96, 0.76] : 0.65 }}
        transition={{ duration: 4, repeat: Infinity, delay: 0.6 }}
        style={{ transformOrigin: '352px 560px' }}
      />
      <circle cx="352" cy="560" r="38" fill="url(#protoNodeN)" opacity="0.5" />
      <ellipse cx="338" cy="543" rx="11" ry="15" fill="rgba(255,255,255,0.1)" />
      <text x="352" y="554" textAnchor="middle" fill="#e2e8f0" fontSize="24" fontWeight="900"
        fontFamily="'Courier New', monospace">8%</text>
      <text x="352" y="573" textAnchor="middle" fill="#c084fc" fontSize="10" fontWeight="700"
        fontFamily="'Courier New', monospace" letterSpacing="2">PROTOCOL</text>

    </svg>
  );
};


// ═══════════════════════════════════════════════════════════════════════════
// 🚽💎 TOILET MODE — SWIRL VORTEX
// viewBox expanded to 480x680 so output nodes are fully visible
// ═══════════════════════════════════════════════════════════════════════════

const ToiletSwirlVortex = ({ isActive, objectCount, distillationsCount, flowingParticles }) => {
  const [angle, setAngle] = useState(0);

  useEffect(() => {
    if (!isActive) { setAngle(0); return; }
    // Slowed from +3 every 16ms → +1.8 every 16ms for a more majestic spin
    const id = setInterval(() => setAngle(a => (a + 1.0) % 360), 16);
    return () => clearInterval(id);
  }, [isActive]);

  const swirlRings = [
    { rx: 72, ry: 25, strokeW: 2.5, speed: 1    },
    { rx: 54, ry: 19, strokeW: 2,   speed: 1.45 },
    { rx: 36, ry: 13, strokeW: 1.5, speed: 2    },
    { rx: 20, ry: 7,  strokeW: 1,   speed: 2.7  },
  ];

  return (
    <svg
      viewBox="0 0 480 680"
      className="distillery-svg toilet-circuit-svg"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', height: '100%' }}
    >
      <defs>
        <linearGradient id="goldGradT" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#FFD700" stopOpacity="1"   />
          <stop offset="50%"  stopColor="#FFA500" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#FF8C00" stopOpacity="0.8" />
        </linearGradient>
        <linearGradient id="goldGradTv" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#FFE44D" stopOpacity="1"   />
          <stop offset="100%" stopColor="#FF8C00" stopOpacity="0.8" />
        </linearGradient>
        <radialGradient id="swirlCoreT" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="30%"  stopColor="#FFD700" stopOpacity="0.85" />
          <stop offset="70%"  stopColor="#FFA500" stopOpacity="0.4"  />
          <stop offset="100%" stopColor="#FF8C00" stopOpacity="0"    />
        </radialGradient>
        <radialGradient id="userNodeT" cx="40%" cy="35%" r="60%">
          <stop offset="0%"   stopColor="#FFD700" stopOpacity="1"   />
          <stop offset="60%"  stopColor="#FFA500" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#FF8C00" stopOpacity="0.3" />
        </radialGradient>
        <radialGradient id="protoNodeT" cx="40%" cy="35%" r="60%">
          <stop offset="0%"   stopColor="#FFD700" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#FF8C00" stopOpacity="0.2" />
        </radialGradient>
        <pattern id="circuitGridT" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#FFD700" strokeWidth="0.4" opacity="0.14" />
        </pattern>
        <filter id="goldGlowT" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="7" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="softT" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="coinGlowT" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="9" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      <rect width="480" height="680" fill="url(#circuitGridT)" />

      {/* TITLE */}
      <text x="240" y="24" textAnchor="middle"
        fill="url(#goldGradT)" fontSize="15" fontWeight="900"
        fontFamily="'Courier New', monospace" letterSpacing="3">
        TOILET WARRIOR MODE
      </text>

      {/* WARRIOR */}
      <motion.g
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
      >
        <motion.circle cx="240" cy="128" r="88"
          fill="rgba(255,215,0,0.06)"
          animate={{ r: isActive ? [86, 98, 86] : 86, opacity: isActive ? [0.5, 0.9, 0.5] : 0.4 }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        {isActive && [1, 2].map(i => (
          <motion.circle key={`wr-${i}`} cx="240" cy="128" r="88"
            fill="none" stroke="#FFD700" strokeWidth="1.5"
            animate={{ scale: [0.909 + i*0.091, 1.091 + i*0.091, 0.909 + i*0.091], opacity: [0.22, 0.52, 0.22] }}
            transition={{ duration: 4, repeat: Infinity, delay: i * 1 }}
          />
        ))}
        <image
          href="/toilet-warrior.png"
          x="140" y="34" width="200" height="200"
          style={{
            filter: isActive
              ? 'drop-shadow(0 0 28px rgba(255,215,0,0.92))'
              : 'drop-shadow(0 0 18px rgba(255,215,0,0.65))'
          }}
        />
      </motion.g>

      {/* CONNECTOR warrior → hex */}
      <motion.line x1="240" y1="234" x2="240" y2="255"
        stroke="#FFD700" strokeWidth="3" filter="url(#softT)"
        animate={{ opacity: [0.45, 1, 0.45] }}
        transition={{ duration: 2.5, repeat: Infinity }}
      />

      {/* HEXAGON COUNT */}
      {isActive && (
        <motion.path d="M 240 248 L 270 265 L 270 300 L 240 317 L 210 300 L 210 265 Z"
          fill="none" stroke="#FFD700" strokeWidth="2"
          animate={{ scale: [1, 1.14, 1], opacity: [0.28, 0.68, 0.28] }}
          transition={{ duration: 2.5, repeat: Infinity }}
          style={{ transformOrigin: '240px 282px' }}
        />
      )}
      <motion.path d="M 240 255 L 266 270 L 266 300 L 240 315 L 214 300 L 214 270 Z"
        fill={isActive ? 'rgba(255,215,0,0.16)' : 'rgba(255,215,0,0.08)'}
        stroke="#FFD700" strokeWidth="2.5" filter="url(#softT)"
        animate={{ opacity: isActive ? [0.8, 1, 0.8] : 0.72 }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      {objectCount > 0 ? (
        <motion.text x="240" y="292" textAnchor="middle"
          fill="#FFD700" fontSize="30" fontWeight="900"
          fontFamily="'Courier New', monospace"
          filter="url(#goldGlowT)"
          animate={{ scale: isActive ? [1, 1.12, 1] : 1 }}
          transition={{ duration: 1.8, repeat: Infinity }}
          style={{ transformOrigin: '240px 285px' }}
        >
          {objectCount}
        </motion.text>
      ) : (
        <text x="240" y="291" textAnchor="middle"
          fill="rgba(255,215,0,0.28)" fontSize="10"
          fontFamily="'Courier New', monospace" letterSpacing="2">READY</text>
      )}

      {/* CONNECTOR hex → bowl */}
      <motion.line x1="240" y1="315" x2="240" y2="334"
        stroke="#FFD700" strokeWidth="3" filter="url(#softT)"
        animate={{ opacity: [0.4, 0.9, 0.4] }}
        transition={{ duration: 2.5, repeat: Infinity }}
      />

      {/* TOILET BOWL */}
      <ellipse cx="240" cy="346" rx="78" ry="16"
        fill="rgba(255,215,0,0.08)" stroke="rgba(255,215,0,0.38)" strokeWidth="2.5" filter="url(#softT)" />
      <path d="M 162 346 Q 162 414 240 422 Q 318 414 318 346 Z"
        fill="rgba(18,9,0,0.88)" stroke="rgba(255,140,0,0.22)" strokeWidth="1.5" />
      <ellipse cx="240" cy="346" rx="68" ry="11"
        fill="rgba(255,215,0,0.04)" stroke="rgba(255,215,0,0.14)" strokeWidth="1" />

      {/* SWIRL RINGS */}
      {swirlRings.map((ring, i) => {
        const dashLen = Math.PI * ring.rx * 0.55;
        const dashGap = Math.PI * ring.rx * 0.45;
        const rot = isActive ? angle * ring.speed * (i % 2 === 0 ? 1 : -1) : 0;
        return (
          <ellipse key={`ring-${i}`}
            cx="240" cy="380" rx={ring.rx} ry={ring.ry}
            fill="none" stroke="#FFD700" strokeWidth={ring.strokeW}
            strokeDasharray={`${dashLen * 0.45} ${dashGap * 0.55}`}
            opacity={isActive ? 0.55 + i * 0.12 : 0.12}
            style={{
              transformOrigin: '240px 380px',
              transform: `rotate(${rot}deg)`,
              transition: isActive ? 'none' : 'opacity 0.6s',
              filter: isActive ? 'drop-shadow(0 0 4px rgba(255,215,0,0.65))' : 'none'
            }}
          />
        );
      })}

      {/* ORBITING PARTICLES */}
      {isActive && [0, 1, 2, 3, 4, 5, 6].map(i => {
        const a = ((angle * 1.6) + i * (360 / 7)) * Math.PI / 180;
        const progress = i / 7;
        const ri  = 68 - progress * 48;
        const eli = ri * 0.348;
        return (
          <circle key={`swirl-${i}`}
            cx={240 + Math.cos(a) * ri}
            cy={380 + Math.sin(a) * eli}
            r={Math.max(1.2, 3.5 - progress * 2.5)}
            fill="#FFD700"
            opacity={0.9 - progress * 0.35}
            filter="url(#softT)"
          />
        );
      })}

      {/* DRAIN */}
      <motion.circle cx="240" cy="380" r="10"
        fill="url(#swirlCoreT)" filter="url(#goldGlowT)"
        animate={{ r: isActive ? [8, 14, 8] : 8, opacity: isActive ? [0.65, 1, 0.65] : 0.4 }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      <circle cx="240" cy="380" r="4" fill="rgba(0,0,0,0.85)" />

      {/* OUTPUT BEAMS — updated endpoints to match new node cy=610 */}
      <motion.path d="M 200 418 L 118 580"
        stroke="url(#goldGradTv)" strokeWidth="3.5" fill="none" filter="url(#softT)"
        strokeDasharray={isActive ? '0' : '7 7'}
        animate={{ opacity: isActive ? [0.55, 1, 0.55] : 0.4 }}
        transition={{ duration: 3.5, repeat: Infinity }}
      />
      <motion.path d="M 280 418 L 362 580"
        stroke="url(#goldGradTv)" strokeWidth="3" fill="none" filter="url(#softT)"
        strokeDasharray={isActive ? '0' : '7 7'}
        animate={{ opacity: isActive ? [0.45, 0.88, 0.45] : 0.35 }}
        transition={{ duration: 3.5, repeat: Infinity, delay: 0.5 }}
      />

      {/* FLOWING PARTICLES — 3-point path through bowl base to node */}
      {flowingParticles.map(particle => (
        <motion.circle key={particle.id} r="5"
          fill="#FFD700" filter="url(#softT)"
          animate={{
            cx: [particle.path === 'left' ? 200 : 280, particle.path === 'left' ? 159 : 321, particle.path === 'left' ? 118 : 362],
            cy: [418, 514, 610],
            opacity: [0, 1, 0],
            scale: [1, 1.3, 0.5]
          }}
          transition={{ duration: 5.5, delay: particle.delay, repeat: Infinity, repeatDelay: 4, ease: 'easeInOut' }}
        />
      ))}

      {/* 92% USER NODE — cy=610 fits in 680 viewBox */}
      {isActive && [1, 2].map(i => (
        <motion.circle key={`urT-${i}`} cx="118" cy="610" r="54"
          fill="none" stroke="#FFD700" strokeWidth="2"
          animate={{ scale: [1, 1.48], opacity: [0.5, 0] }}
          transition={{ duration: 4, repeat: Infinity, delay: i * 1.6 }}
        />
      ))}
      <motion.circle cx="118" cy="610" r="54"
        fill="rgba(10,5,0,0.93)" stroke="#FFD700" strokeWidth="3"
        filter="url(#coinGlowT)"
        animate={{ scale: isActive ? [1, 1.06, 1] : 1, opacity: isActive ? [0.87, 1, 0.87] : 0.75 }}
        transition={{ duration: 4, repeat: Infinity }}
        style={{ transformOrigin: '118px 610px' }}
      />
      <circle cx="118" cy="610" r="46" fill="url(#userNodeT)" opacity="0.55" />
      <ellipse cx="104" cy="593" rx="13" ry="17" fill="rgba(255,255,255,0.12)" />
      <text x="118" y="603" textAnchor="middle" fill="#1a0a00" fontSize="26" fontWeight="900"
        fontFamily="'Courier New', monospace" filter="url(#goldGlowT)">92%</text>
      <text x="118" y="624" textAnchor="middle" fill="#FFD700" fontSize="11" fontWeight="700"
        fontFamily="'Courier New', monospace" letterSpacing="2">💎 USER</text>

      {/* 8% PROTOCOL NODE — cy=610 */}
      <motion.circle cx="362" cy="610" r="44"
        fill="rgba(10,5,0,0.9)" stroke="#FFA500" strokeWidth="2.5" filter="url(#softT)"
        animate={{ scale: isActive ? [1, 1.05, 1] : 1, opacity: isActive ? [0.76, 0.96, 0.76] : 0.65 }}
        transition={{ duration: 4, repeat: Infinity, delay: 0.6 }}
        style={{ transformOrigin: '362px 610px' }}
      />
      <circle cx="362" cy="610" r="36" fill="url(#protoNodeT)" opacity="0.5" />
      <ellipse cx="349" cy="595" rx="11" ry="14" fill="rgba(255,255,255,0.1)" />
      <text x="362" y="604" textAnchor="middle" fill="#FFD700" fontSize="22" fontWeight="900"
        fontFamily="'Courier New', monospace">8%</text>
      <text x="362" y="622" textAnchor="middle" fill="#FFA500" fontSize="10" fontWeight="700"
        fontFamily="'Courier New', monospace" letterSpacing="2">PROTOCOL</text>
    </svg>
  );
};

export default Distillery;
